/**
 * フォーム定義(データ駆動)。Form.tsx が variant で引く。
 * 先例: D:/AI/site-tabishitsuji/src/pages/contact/page.tsx。要点は docs/_qa/_form_research_tabishitsuji.md。
 * 2026-09-17 Yuichi 再指摘: ご用件はプルダウンでなくカードで切替・カードを選ぶまで項目を出さない・「ご希望の連絡方法」は無し。
 * 事実の出典(Yuichi 2026-09-17): 送迎は執事が運転する黒のアルファードで指定5駅・施設との往復のみ。送迎はご利用のプランに付随するサービス(オプション扱いではない・料金の話は書かない)。車両を使えない日があるため、予約時に可否を確認する(v3・Yuichi 2026-09-17)。
 * サロンは女性限定。スタジオは最大6名・9〜21時。見習いはモーニングコート貸出(身長・普段のサイズ)。ショップは注文番号。
 */
export type FormFieldDef = {
  name: string
  label: string
  /** checkgroup: options を複数選択のチェック群として出す(送信は選択ラベルを「 / 」で 1 行に) */
  type?: 'text' | 'email' | 'tel' | 'date' | 'textarea' | 'select' | 'number' | 'checkbox' | 'file' | 'checkgroup'
  /** type=file: 受け付ける拡張子 */
  accept?: string
  required?: boolean
  placeholder?: string
  options?: string[]
  hint?: string
  /** 英語版(/en/)。未設定なら forms.en.ts の name 別辞書を使う(仕様 §2-8) */
  labelEn?: string
  placeholderEn?: string
  optionsEn?: string[]
  hintEn?: string
}

export type TopicKey = 'salon' | 'studio' | 'apprentice' | 'shop' | 'media' | 'other' | 'recruit'
export type FormVariant = 'global' | 'salon' | 'studio' | 'apprentice' | 'shop' | 'media' | 'recruit'

export type TopicDef = {
  key: TopicKey
  label: string
  /** ご用件カードの英字(旅執事 contact-method カードの見出し位置) */
  en: string
  /** ご用件カードの一言(2行以内) */
  card: string
  /** 種別を選んだ直後に出す一言(何を伺うか) */
  lead?: string
  /** 種別固有の項目(共通項目の後・本文の前に差し込む) */
  fields: FormFieldDef[]
  /** 同意文に添えるキャンセルポリシー */
  cancelUrl?: string
  /** 本文欄のラベル(既定: お問い合わせ内容) */
  messageLabel?: string
  messageRequired?: boolean
  /** 本文欄のプレースホルダー(既定: commonMessage.placeholder) */
  messagePlaceholder?: string
}

/** 共通項目(先頭側) */
export const commonHead: FormFieldDef[] = [
  { name: 'name', label: 'お名前', required: true, placeholder: '例)山田 花子' },
  { name: 'furigana', label: 'ふりがな', required: true, placeholder: '例)やまだ はなこ' },
  { name: 'email', label: 'メールアドレス', type: 'email', required: true, placeholder: '例)hanako@example.com' },
  { name: 'tel', label: '電話番号', type: 'tel', required: true, placeholder: '例)09012345678' },
]

/** 共通項目(末尾側・本文)。ラベルは topic で差し替え可 */
export const commonMessage: FormFieldDef = { name: 'message', label: 'お問い合わせ内容', type: 'textarea', placeholder: 'ご質問やご要望を、ご自由にお書きくださいませ。' }

export const pickupPlaces = ['東京テレポート駅', '東京国際クルーズターミナル駅', 'ダイバーシティ東京 プラザ', 'アクアシティお台場', 'デックス東京ビーチ']

export const topics: Record<TopicKey, TopicDef> = {
  salon: {
    key: 'salon',
    en: 'Salon',
    card: '女性限定。執事とのお時間のご予約・ご相談',
    label: '執事サロン(ご予約・ご相談)',
    lead: '執事サロンは女性のお客様限定でございます。ご希望の日時とお過ごし方を伺い、執事が空き状況を確かめてご返信いたします。',
    cancelUrl: '/salon/price/cancel-policy/',
    fields: [
      { name: 'dates', label: 'ご希望日時(第1〜第3希望)', type: 'textarea', required: true, placeholder: '例)10月3日 14時〜18時 / 10月10日 同時刻', hint: 'ご希望として承り、執事が空き状況を確かめてご返信いたします。' },
      { name: 'people', label: '人数', type: 'select', required: true, options: ['1名', '2名', '3名', '4名'] },
      { name: 'experience', label: 'ご希望の過ごし方', type: 'select', options: ['お任せ', '執事のおもてなし(紅茶とくつろぎ)', '午後の紅茶', '食卓にて', 'シアター', '執事の心づかい', '執事とのお出かけ(4時間以上)', '深夜のご滞在(1名限定)', '執事と料理レッスン', '記念撮影'] },
      { name: 'butler_pref', label: '執事の性別・年代のご希望', type: 'select', options: ['特になし', '女性の執事', '男性の執事', '若い執事', '年を重ねた執事'] },
      { name: 'buddy', label: '執事(バディ)のご指名', placeholder: '例)いつもの執事、または執事のお名前' },
      { name: 'membership', label: '会員のご入会のご相談', type: 'select', options: ['なし(ビジター)', 'ブロンズ', 'シルバー', 'ゴールド', 'プラチナ', 'ランクの相談'] },
    ],
    messageLabel: 'ご要望・ご質問',
  },
  studio: {
    key: 'studio',
    en: 'Studio',
    card: '撮影・会議・控室などのご予約・ご相談',
    label: 'スタジオ・レンタルスペース(ご予約・ご相談)',
    lead: '最大6名さま・9時〜21時でご利用いただけます。お迎え・お送りは執事が運転する黒のアルファードで、下記5か所との往復のみ承ります。送迎はご利用のプランに付随するサービスです。車両を使えない日がございますので、ご予約時に可否を確認いたします。',
    cancelUrl: '/studio/price/cancel-policy/',
    fields: [
      { name: 'usage', label: '用途', type: 'select', required: true, options: ['撮影', '会議', '控室', 'ロケ', '収録', 'その他'] },
      { name: 'plan', label: '希望するプラン', type: 'select', options: ['未定(執事へ相談)', 'スタンダード(3時間)', '結婚式・お祝いの撮影(4時間)', '執事服ポートレート(3時間)', '会議・控室(半日・4時間)', 'ロケ・終日(8時間)'] },
      { name: 'dates', label: 'ご希望日時(第1〜第3希望)', type: 'textarea', required: true, placeholder: '例)10月3日 10時〜13時 / 10月10日 同時刻', hint: '9時〜21時の範囲で承ります。' },
      { name: 'people', label: '人数(撮影者・同行者を含め最大6名)', type: 'select', required: true, options: ['1名', '2名', '3名', '4名', '5名', '6名'] },
      { name: 'costume_use', label: '執事服の着用', type: 'select', required: true, options: ['着用しない', '着用する', '相談したい'] },
      { name: 'costume_size', label: '執事服を着る方の身長・普段のお召し物・靴のサイズ', placeholder: '例)165cm・Mサイズ・24.5cm' },
      { name: 'pickup', label: 'お迎え・お送り(執事が運転する黒のアルファード)', type: 'select', required: true, options: ['不要', ...pickupPlaces.map((place) => `希望する(${place})`), '相談したい'], hint: '上記5か所との往復のみ承ります。送迎はご利用のプランに付随するサービスです。車両を使えない日がございますので、ご予約時に可否を確認いたします。' },
      { name: 'pickup_details', label: 'お迎え・お送りの人数・お荷物(希望する場合)', placeholder: '例)3名・撮影機材ケース2つ' },
      { name: 'equipment', label: '持ち込む撮影機材・商品・衣装', placeholder: '例)照明2灯・商品サンプル10点' },
      { name: 'extension', label: '延長や執事の出演に関する希望', placeholder: '例)執事に給仕の所作で出演してほしい' },
    ],
    messageLabel: '撮影の目的・ご要望',
  },
  apprentice: {
    key: 'apprentice',
    en: 'Apprentice',
    card: 'モーニングコートで学ぶ体験のご予約・ご相談',
    label: '執事見習い体験(ご予約・ご相談)',
    lead: 'モーニングコートをお貸しいたします。サイズと種類に限りがございますので、身長と普段のお召し物のサイズをお知らせくださいませ。',
    cancelUrl: '/apprentice/price/cancel-policy/',
    fields: [
      { name: 'dates', label: 'ご希望日時(第1〜第3希望)', type: 'textarea', required: true, placeholder: '例)10月3日 14時〜 / 10月10日 同時刻' },
      { name: 'people', label: '人数', type: 'select', required: true, options: ['1名', '2名', '3名', '4名'] },
      { name: 'height', label: '身長(cm)', type: 'number', required: true, placeholder: '例)168' },
      { name: 'size', label: '普段のお召し物のサイズ', type: 'select', required: true, options: ['S', 'M', 'L', 'LL', '3L以上', 'わからない(相談)'] },
      { name: 'build', label: '体型', type: 'select', required: true, options: ['細め', '標準', 'がっしり'] },
      { name: 'shoe', label: '靴のサイズ(cm)', type: 'number', required: true, placeholder: '例)25.5' },
      { name: 'gender_size', label: '衣装', type: 'select', required: true, options: ['男性用', '女性用'] },
      { name: 'butler_pref', label: '執事の性別のご希望', type: 'select', options: ['特になし', '女性の執事', '男性の執事'] },
      { name: 'gift', label: '贈り物としてのお申し込み', type: 'select', options: ['いいえ(ご本人)', 'はい(贈る相手のサイズを上に記入)'] },
    ],
    messageLabel: 'ご要望・ご質問',
  },
  shop: {
    key: 'shop',
    en: 'Shop',
    card: 'ご注文・商品・サイズについてのお問い合わせ',
    label: 'オンラインショップ(ご注文・商品について)',
    lead: 'ご注文後のお問い合わせは注文番号と商品名をお知らせくださいませ。不良品・誤印刷・配送時の破損・紛失は製造元(Printful)のカスタマーサポートへ直接ご連絡くださいませ(当協会は製造・配送の対応窓口ではございません)。カード番号などのご決済情報はお書きにならないでください。',
    cancelUrl: '/shop/guide/cancel-policy/',
    fields: [
      { name: 'order_number', label: '注文番号', placeholder: '例)QB-12345(ご注文後の場合)' },
      { name: 'product_name', label: '商品名', placeholder: '例)執事の紅茶缶' },
      { name: 'inquiry_type', label: '内容', type: 'select', required: true, options: ['ご注文前の商品・配送のご相談', '製造開始前のご注文内容変更(色・サイズ・数量・お届け先)', 'ご注文・お支払い', 'サイズ・仕様', 'ギフト', 'その他'] },
    ],
    messageLabel: 'お問い合わせ内容',
    messageRequired: true,
  },
  media: {
    key: 'media',
    en: 'Media',
    card: '取材・掲載のご依頼',
    label: 'メディア・取材',
    lead: '取材・掲載のご依頼を承ります。媒体と企画の趣旨、掲載時期、ご希望の取材日と内容をお知らせくださいませ。',
    fields: [
      { name: 'media_name', label: '媒体名', required: true, placeholder: '例)○○テレビ「番組名」 / 雑誌名 / Web 媒体名' },
      { name: 'company', label: '会社名・所属', placeholder: '例)株式会社○○ 編集部' },
      { name: 'project', label: '企画・趣旨', type: 'textarea', required: true, placeholder: '企画の概要と、当館を取り上げていただく趣旨' },
      { name: 'publish_date', label: '掲載・放送の時期', placeholder: '例)11月号 / 12月中旬放送' },
      { name: 'shoot_dates', label: '取材希望日(候補)', placeholder: '例)10月3日 午後 / 10月10日 終日' },
      { name: 'coverage', label: '希望する取材内容', type: 'select', required: true, options: ['館内の撮影', '執事へのインタビュー', 'サービスの体験取材', '写真・素材の提供', 'その他'] },
      { name: 'attachments', label: '企画書・資料の添付', type: 'file', accept: '.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.jpg,.jpeg,.png', hint: 'PDF・Office 文書・画像。3 点まで、合計 10MB まで。' },
    ],
    messageLabel: 'ご要望・ご質問',
  },
  other: {
    key: 'other',
    en: 'Other',
    card: '上記に当てはまらないご用件',
    label: 'その他',
    lead: 'ご用件をお書きくださいませ。担当の執事よりご返信いたします。フォームでのご相談が難しい場合は、info@queensbutler.jp まで直接ご連絡くださいませ。', // Yuichi 2026-09-17「info についてはその他の相談かうまく問い合わせができない人にこれを連携しろ」
    fields: [],
    messageLabel: 'お問い合わせ内容',
    messageRequired: true,
  },
  recruit: {
    key: 'recruit',
    en: 'Recruit',
    card: '執事の求人へのご応募',
    label: '執事の求人への応募',
    lead: '履歴書・職務経歴書と、上半身・全身のお写真をご添付ください(各 8MB まで／PDF または画像)。',
    // Yuichi 2026-09-17「リクルートは the butler のフォームと同じようにつくって」「添付場所は the butler にあわせて」: 項目・順序・添付位置は D:/AI/site-thebutler/src/pages/RecruitEntryPage.tsx(skillOptions / attachmentFields / experienceLevels)と同じ
    fields: [
      { name: 'age', label: '年齢', placeholder: '例)28' },
      { name: 'experience', label: '執事・接客のご経験', type: 'select', required: true, options: ['未経験', '接客・ホスピタリティ業界の経験あり', '秘書・資産管理などの実務経験あり', '執事・バトラーの経験あり'] },
      { name: 'skills', label: '保有スキル・資格', type: 'checkgroup', hint: '当てはまるものをすべて選択', options: ['語学力(英語など外国語での接客・会話)', '普通自動車運転免許', '秘書・接遇・マナーの資格(秘書検定・サービス接遇検定など)', 'ホテル・レストラン・ブライダル等の接客実務', '調理・ワイン・テーブルコーディネートの知識', '家事・整理収納・クリーニング等の家政スキル', 'PC・事務(Word / Excel・スケジュール管理など)'] },
      { name: 'skills_detail', label: 'その他の資格・実績', placeholder: 'その他の資格・語学レベル・具体的な実績などをご記入ください' },
      { name: 'resume', label: '提出書類・写真: 履歴書', type: 'file', required: true, accept: '.pdf,.jpg,.jpeg,.png,.heic,.doc,.docx', hint: '書類・写真はすべて必須。各 8MB まで(PDF または画像)。' },
      { name: 'cv', label: '職務経歴書', type: 'file', required: true, accept: '.pdf,.jpg,.jpeg,.png,.heic,.doc,.docx' },
      { name: 'photo_face', label: '上半身の写真', type: 'file', required: true, accept: 'image/*' },
      { name: 'photo_full', label: '全身の写真', type: 'file', required: true, accept: 'image/*' },
    ],
    messageLabel: '志望動機・自己PR',
    messagePlaceholder: '執事を志望される理由や、これまでのご経験などをお聞かせください。',
    messageRequired: true,
  },
}

export type VariantDef = {
  /** 「ご用件」カードの並び(順序どおり)。初期状態は未選択(カードを選ぶまで項目を出さない・2026-09-17 Yuichi) */
  topics: TopicKey[]
  /** 種別セレクトのラベル */
  subjectLabel: string
  /** 送信種別(GAS 側の振り分け) */
  formType: 'contact' | 'recruit'
  /** 共通項目のうち省く name(応募フォームでふりがな等を使わない場合) */
  omitCommon?: string[]
}

export const variants: Record<FormVariant, VariantDef> = {
  global: { topics: ['salon', 'studio', 'apprentice', 'shop', 'recruit', 'media', 'other'], subjectLabel: 'ご用件', formType: 'contact' }, // Yuichi 2026-09-17「共通の問い合わせではリクルートも追加してほしい」
  salon: { topics: ['salon', 'media'], subjectLabel: 'ご用件', formType: 'contact' },
  studio: { topics: ['studio', 'media'], subjectLabel: 'ご用件', formType: 'contact' },
  apprentice: { topics: ['apprentice', 'media'], subjectLabel: 'ご用件', formType: 'contact' },
  shop: { topics: ['shop', 'media'], subjectLabel: 'ご用件', formType: 'contact' },
  media: { topics: ['media'], subjectLabel: 'ご用件', formType: 'contact' },
  recruit: { topics: ['recruit', 'media'], subjectLabel: 'ご用件', formType: 'recruit' },
}

/** 旧 service 値 → variant(本文 JSON に variant が無い場合の後方互換) */
export function variantFromService(service?: string): FormVariant {
  if (service === 'common' || service === 'general' || !service) return 'global'
  if (service in variants) return service as FormVariant
  return 'global'
}
